import { type ReactNode, useState } from 'react';
import {
    type GestureResponderEvent,
    type LayoutChangeEvent,
    Modal,
    TouchableWithoutFeedback,
    useWindowDimensions,
    View,
} from 'react-native';
import {
    Dialog,
    isWeb,
    type StackProps,
    Tooltip as TamaguiTooltip,
    XStack,
    YStack,
} from 'tamagui';

import {
    type TooltipContent,
    useTooltipController,
} from '../../hooks/useTooltipController';
import { InfoButton } from '../buttons/Button';
import { BodyMediumRegular, BodySmallRegular } from '../text/Typography';

const tooltipBackgroundColor = '$accent1000';
const tooltipOpacity = 0.9;

const tooltipStyles: StackProps = {
    animation: 'quick',
    animateOnly: ['transform', 'opacity'],
    enterStyle: { y: 10, opacity: 0, scale: 0.95 },
    exitStyle: { y: 10, opacity: 0, scale: 0.95 },
    backgroundColor: tooltipBackgroundColor,
    opacity: tooltipOpacity,
    borderRadius: 8,
    padding: 12,
};

const TooltipContentComponent = ({
    tooltipContent,
}: {
    tooltipContent: TooltipContent;
}) => {
    if (Array.isArray(tooltipContent)) {
        return (
            <YStack gap={8} width="100%" minWidth={180}>
                {tooltipContent.map((row) => (
                    <XStack
                        key={row.label}
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={16}
                    >
                        <BodySmallRegular color="$white">
                            {row.label}
                        </BodySmallRegular>
                        <BodySmallRegular color="$white" textAlign="right">
                            {row.value}
                        </BodySmallRegular>
                    </XStack>
                ))}
            </YStack>
        );
    }

    return <BodySmallRegular color="$white">{tooltipContent}</BodySmallRegular>;
};

export const TooltipTrigger = ({
    tooltipContent,
    tooltipGroupName = 'default',
    tooltipNativeVerticalOffset = 0,
    children,
}: {
    tooltipContent: TooltipContent;
    tooltipGroupName?: string;
    tooltipNativeVerticalOffset?: number;
    children?: ReactNode;
}) => {
    const {
        setIsTooltipOpen,
        setTooltipContent,
        setTooltipTopPosition,
        setTooltipLeftPosition,
    } = useTooltipController({ tooltipGroupName });

    return isWeb ? (
        <TamaguiTooltip>
            <TamaguiTooltip.Trigger>{children}</TamaguiTooltip.Trigger>
            <TamaguiTooltip.Content {...tooltipStyles} maxWidth={350}>
                <TamaguiTooltip.Arrow />
                <TooltipContentComponent tooltipContent={tooltipContent} />
            </TamaguiTooltip.Content>
        </TamaguiTooltip>
    ) : (
        <XStack
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            onPress={(e: GestureResponderEvent) => {
                e.currentTarget.measureInWindow(
                    (x, y, triggerWidth, triggerHeight) => {
                        setTooltipLeftPosition(x + triggerWidth / 2);
                        setTooltipTopPosition(
                            y + triggerHeight - 2 + tooltipNativeVerticalOffset,
                        );
                        setTooltipContent(tooltipContent);
                        setIsTooltipOpen(true);
                    },
                );
            }}
        >
            {children}
        </XStack>
    );
};

export const InfoButtonWithTooltip = ({
    tooltipContent,
    tooltipGroupName = 'default',
    text,
    subtle = false,
}: {
    tooltipContent: TooltipContent;
    tooltipGroupName?: string;
    text?: string;
    subtle?: boolean;
}) => {
    return (
        <TooltipTrigger
            tooltipContent={tooltipContent}
            tooltipGroupName={tooltipGroupName}
        >
            {text != null && (
                <BodyMediumRegular marginRight={-4}>{text}</BodyMediumRegular>
            )}
            {/* 'disabled' so InfoButton onPress from doesn't prevent TooltipTrigger onPress  */}
            <InfoButton disabled subtle={subtle} />
        </TooltipTrigger>
    );
};

export const TooltipGroupProvider = ({
    tooltipGroupName = 'default',
}: {
    tooltipGroupName?: string;
}) => {
    const nativeTooltipArrowSize = 8;
    const nativeTooltipWidthFactor = 0.9;
    const nativeTooltipBottomMargin = 16;
    const layout = useWindowDimensions();
    const nativeTooltipWidth = layout.width * nativeTooltipWidthFactor;
    const spaceBetweenWindowAndTooltip =
        (layout.width * (1 - nativeTooltipWidthFactor)) / 2;

    const {
        isTooltipOpen,
        setIsTooltipOpen,
        tooltipContent,
        tooltipTopPosition,
        tooltipLeftPosition,
    } = useTooltipController({ tooltipGroupName });

    // Measured after the tooltip box first renders (see onLayout below), so
    // we can clamp its top position to the actual content height rather than
    // an estimate — content length varies (a single line vs. several detail
    // rows), so a fixed offset can't account for it.
    const [tooltipContentHeight, setTooltipContentHeight] = useState<
        number | null
    >(null);

    const arrowLeft =
        0 -
        spaceBetweenWindowAndTooltip -
        nativeTooltipArrowSize / 2 +
        tooltipLeftPosition;

    // Keep the tooltip fully on-screen: a trigger near the bottom of a list
    // (e.g. the last account row) can otherwise open a tooltip that's cut
    // off below the screen edge, with no way to scroll it into view (ENG-6866).
    // Falls back to the unclamped position until the first layout measures
    // the real height.
    const clampedTooltipTopPosition =
        tooltipContentHeight == null
            ? tooltipTopPosition
            : Math.max(
                  0,
                  Math.min(
                      tooltipTopPosition,
                      layout.height -
                          tooltipContentHeight -
                          nativeTooltipBottomMargin,
                  ),
              );

    const handleTooltipLayout = (event: LayoutChangeEvent) => {
        setTooltipContentHeight(event.nativeEvent.layout.height);
    };

    if (isWeb) {
        return (
            <Dialog open={isTooltipOpen} onOpenChange={setIsTooltipOpen} modal>
                <Dialog.Portal justifyContent="flex-start">
                    <Dialog.Overlay
                        key="overlay"
                        opacity={0}
                        onPress={() => {
                            setIsTooltipOpen(false);
                        }}
                    />
                    <Dialog.Content
                        key="content"
                        {...tooltipStyles}
                        width={nativeTooltipWidth}
                        top={tooltipTopPosition}
                    >
                        <XStack
                            rotate="45deg"
                            position="absolute"
                            width={nativeTooltipArrowSize}
                            height={nativeTooltipArrowSize}
                            top={-nativeTooltipArrowSize / 2}
                            left={arrowLeft}
                            backgroundColor={tooltipBackgroundColor}
                            opacity={tooltipOpacity}
                        />
                        <TooltipContentComponent
                            tooltipContent={tooltipContent}
                        />
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog>
        );
    }

    // Native: use RN Modal so the tooltip renders at the true window root.
    // This means measureInWindow coordinates are always correct, regardless of
    // which sheet, scroll view, or nested modal the trigger lives inside.
    return (
        <Modal
            visible={isTooltipOpen}
            transparent
            animationType="none"
            statusBarTranslucent
            onRequestClose={() => {
                setIsTooltipOpen(false);
            }}
        >
            {/* Invisible full-screen backdrop to dismiss on tap-outside */}
            <TouchableWithoutFeedback
                onPress={() => {
                    setIsTooltipOpen(false);
                }}
            >
                <View
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                    }}
                />
            </TouchableWithoutFeedback>

            <YStack
                position="absolute"
                top={clampedTooltipTopPosition}
                width={nativeTooltipWidth}
                left={spaceBetweenWindowAndTooltip}
                onLayout={handleTooltipLayout}
                {...tooltipStyles}
            >
                <XStack
                    rotate="45deg"
                    position="absolute"
                    width={nativeTooltipArrowSize}
                    height={nativeTooltipArrowSize}
                    top={-nativeTooltipArrowSize / 2}
                    left={arrowLeft}
                    backgroundColor={tooltipBackgroundColor}
                    opacity={tooltipOpacity}
                />
                <TooltipContentComponent tooltipContent={tooltipContent} />
            </YStack>
        </Modal>
    );
};
